import React, { useEffect, useMemo, useState } from "react";
import { useTable, useFilters, useSortBy } from "react-table";

import api, { handleApi } from "pages/api/api";
import { useAlert } from "pages/context/AlertContext";
import { useAuth } from "pages/context/AuthContext";
import PageHeader from "pages/components/PageHeader";
import createAutocompleteFilter from "pages/utils/autocompleteFilter";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

import Card from "@mui/material/Card";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";

import BlockIcon from "@mui/icons-material/Block";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

function TextFilter({ column: { filterValue, setFilter } }) {
  return (
    <input
      value={filterValue || ""}
      onChange={(e) => setFilter(e.target.value || undefined)}
      placeholder="filtrar..."
      style={{
        width: "100%",
        padding: "6px 4px",
        border: "none",
        borderBottom: "1px solid rgba(0,0,0,0.2)",
        outline: "none",
        background: "transparent",
        fontSize: 12,
        color: "#556",
      }}
    />
  );
}

function EstadoFilter({ column: { filterValue, setFilter } }) {
  return (
    <select
      value={filterValue || ""}
      onChange={(e) => setFilter(e.target.value || undefined)}
      style={{
        width: "100%",
        padding: "6px 4px",
        border: "none",
        borderBottom: "1px solid rgba(0,0,0,0.2)",
        outline: "none",
        background: "transparent",
        fontSize: 12,
        color: "#556",
      }}
    >
      <option value="">Todas</option>
      <option value="0">Ativas</option>
      <option value="1">Inativas</option>
    </select>
  );
}

const FILTERS_KEY = "tbl:submarcas:filters";
const KEEP_KEY = "tbl:submarcas:keep";

const autocompleteFilterFabricantes = createAutocompleteFilter(
  (option) => `${option?.ref ?? ""} ${option?.nome ?? ""}`
);

export default function Submarcas() {
  const { showAlert } = useAlert();
  const { temPermissao } = useAuth();

  const [fabricantes, setFabricantes] = useState([]);
  const [fabricanteSel, setFabricanteSel] = useState(null);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const initialKeep =
    (() => {
      try {
        const v = localStorage.getItem(KEEP_KEY);
        return v === null ? true : JSON.parse(v);
      } catch {
        return true;
      }
    })() ?? true;

  const [keepFilters, setKeepFilters] = useState(initialKeep);

  const initialFilters = keepFilters
    ? (() => {
        try {
          return JSON.parse(localStorage.getItem(FILTERS_KEY) || "[]");
        } catch {
          return [];
        }
      })()
    : [];

  const carregarFabricantes = async () => {
    const [payload, err] = await handleApi(api.post("Comercial/SelFabricantesCombo", {}));

    if (err) {
      showAlert(err, "error");
      return;
    }

    setFabricantes(Array.isArray(payload) ? payload : []);
  };

  const carregarSubmarcas = async (fabricante) => {
    if (!fabricante?.ref) {
      setData([]);
      return;
    }

    setLoading(true);

    const [payload, err] = await handleApi(
      api.post("Comercial/SelSubmarcasPorFabricanteAdmin", {
        reffabricante: fabricante.ref,
      })
    );

    setLoading(false);

    if (err) {
      showAlert(err, "error");
      return;
    }

    setData(Array.isArray(payload) ? payload : []);
  };

  useEffect(() => {
    carregarFabricantes();
  }, []);

  const atualizarEstado = async (row) => {
    const novaInativa = Number(row?.inativa || 0) === 1 ? 0 : 1;

    const [_, err, meta] = await handleApi(
      api.post("Comercial/UpdSubmarcaInativa", {
        refsubmarca: row.ref,
        inativa: novaInativa,
      })
    );

    if (err) {
      showAlert(err, "error");
      return;
    }

    if (meta?.message) showAlert(meta.message, meta.severity || "success");

    await carregarSubmarcas(fabricanteSel);
  };

  const columns = useMemo(
    () => [
      {
        Header: "",
        id: "acoes",
        disableFilters: true,
        width: 72,
        Cell: ({ row }) => {
          const sm = row.original;
          const inativa = Number(sm?.inativa || 0) === 1;

          return (
            <IconButton
              color={inativa ? "secondary" : "error"}
              size="small"
              disabled={!temPermissao("Menu Administração", "u")}
              onClick={() => atualizarEstado(sm)}
              title={inativa ? "Ativar" : "Inativar"}
            >
              <BlockIcon fontSize="small" />
            </IconButton>
          );
        },
      },
      { Header: "Ref.", accessor: "ref", Filter: TextFilter },
      { Header: "Nome", accessor: "nome", Filter: TextFilter },
      {
        Header: "Estado",
        accessor: "inativa",
        Filter: EstadoFilter,
        Cell: ({ value }) =>
          Number(value || 0) === 1 ? (
            <BlockIcon style={{ color: "black" }} fontSize="small" />
          ) : (
            <CheckCircleIcon style={{ color: "black" }} fontSize="small" />
          ),
      },
    ],
    [temPermissao, fabricanteSel]
  );

  const table = useTable(
    {
      columns,
      data,
      initialState: { filters: initialFilters },
      autoResetFilters: false,
      autoResetSortBy: false,
    },
    useFilters,
    useSortBy
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow, setAllFilters, state } =
    table;

  useEffect(() => {
    if (!keepFilters && (state.filters?.length || 0) > 0) {
      setAllFilters([]);
      try {
        localStorage.removeItem(FILTERS_KEY);
      } catch {}
    }
  }, [data]);

  useEffect(() => {
    try {
      localStorage.setItem(KEEP_KEY, JSON.stringify(keepFilters));
    } catch {}

    if (!keepFilters) {
      setAllFilters([]);
      try {
        localStorage.removeItem(FILTERS_KEY);
      } catch {}
    }
  }, [keepFilters, setAllFilters]);

  useEffect(() => {
    if (!keepFilters) return;

    try {
      localStorage.setItem(FILTERS_KEY, JSON.stringify(state.filters || []));
    } catch {}
  }, [state.filters, keepFilters]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <PageHeader />

      <MDBox pt={1}>
        <Card>
          <MDBox p={3}>
            <MDBox display="flex" alignItems="center" gap={2} mb={2}>
              <MDBox width="420px">
                <Autocomplete
                  size="small"
                  options={fabricantes}
                  value={fabricanteSel}
                  filterOptions={autocompleteFilterFabricantes}
                  getOptionLabel={(opt) =>
                    opt?.nome ? `${opt.nome} (${opt.ref})` : opt?.ref || ""
                  }
                  isOptionEqualToValue={(opt, val) => opt?.ref === val?.ref}
                  onChange={async (_, value) => {
                    setFabricanteSel(value);
                    await carregarSubmarcas(value);
                  }}
                  renderInput={(params) => <TextField {...params} label="Fabricante" />}
                />
              </MDBox>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={keepFilters}
                    onChange={(e) => setKeepFilters(e.target.checked)}
                    size="small"
                  />
                }
                label="Manter filtros"
              />

              {loading && <CircularProgress size={22} />}
            </MDBox>

            {!fabricanteSel && (
              <MDTypography variant="button" color="text">
                Seleciona um fabricante para consultar as submarcas associadas.
              </MDTypography>
            )}

            {fabricanteSel && (
              <MDBox sx={{ height: "68vh", overflow: "auto", borderRadius: 1 }}>
                <table {...getTableProps()} style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    {headerGroups.map((hg) => (
                      <tr {...hg.getHeaderGroupProps()}>
                        {hg.headers.map((col) => (
                          <th
                            {...col.getHeaderProps(col.getSortByToggleProps())}
                            style={{
                              position: "sticky",
                              top: 0,
                              zIndex: 2,
                              background: "#fff",
                              textAlign: "left",
                              padding: "6px 8px",
                              fontSize: 12,
                              color: "#7b809a",
                              verticalAlign: "bottom",
                              boxShadow: "0 2px 4px rgba(0,0,0,0.06)",
                              width: col.id === "acoes" ? 72 : undefined,
                            }}
                          >
                            <div style={{ display: "flex", alignItems: "center" }}>
                              {col.render("Header")}
                              {col.isSorted ? (
                                col.isSortedDesc ? (
                                  <span style={{ marginLeft: 6 }}>▼</span>
                                ) : (
                                  <span style={{ marginLeft: 6 }}>▲</span>
                                )
                              ) : null}
                            </div>

                            {!col.disableFilters && (
                              <div onClick={(e) => e.stopPropagation()} style={{ marginTop: 4 }}>
                                {col.canFilter ? col.render("Filter") : null}
                              </div>
                            )}
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>

                  <tbody {...getTableBodyProps()}>
                    {rows.map((row) => {
                      prepareRow(row);

                      return (
                        <tr {...row.getRowProps()}>
                          {row.cells.map((cell) => (
                            <td
                              {...cell.getCellProps()}
                              style={{
                                padding: "8px 10px",
                                borderTop: "1px solid #eee",
                                fontSize: 12,
                                color: "#1a2b4c",
                                whiteSpace: "nowrap",
                                width: cell.column.id === "acoes" ? 72 : undefined,
                              }}
                            >
                              {cell.render("Cell")}
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </MDBox>
            )}
          </MDBox>
        </Card>
      </MDBox>
    </DashboardLayout>
  );
}
